import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // <-- 1. Importar useNavigate
import axios from 'axios';

const AuthContext = createContext(null);

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Solo poner JSON si NO es FormData
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

const applyOrgTheme = (theme) => {
  if (!theme || !theme.accent_primary) return;
  const root = document.documentElement;
  root.style.setProperty('--org-bg',       theme.bg             || '#060810');
  root.style.setProperty('--org-accent',   theme.accent_primary  || '#00FFFF');
  root.style.setProperty('--org-accent2',  theme.accent_secondary || '#0066CC');
  root.style.setProperty('--org-accent3',  theme.accent_tertiary  || '#00FFCC');
  root.style.setProperty('--org-text',     theme.text_color      || '#B3E5FF');
  root.style.setProperty('--org-card-bg',  theme.card_bg         || 'rgba(0,255,255,0.08)');
  root.style.setProperty('--org-font',     theme.font_family     || "'DM Sans', sans-serif");
  root.style.setProperty('--org-scan',     theme.scan_color      || 'rgba(0,255,255,0.4)');
  root.style.setProperty('--org-badge-bg', theme.badge_bg        || 'rgba(0,100,255,0.2)');
  root.style.setProperty('--org-badge',    theme.badge_color     || '#00FFFF');

  if (theme.bg_image_url) {
    root.style.setProperty('--org-bg-image', `url('${theme.bg_image_url}')`);
  } else {
    root.style.setProperty('--org-bg-image', "url('/bg-dashboard.png')");
  }
  if (theme.bg_gradient) {
    root.style.setProperty('--org-bg-gradient', theme.bg_gradient);
  }

  document.body.style.fontFamily = theme.font_family || "'DM Sans', sans-serif";
  document.body.classList.add('org-themed');
  document.body.style.background = theme.bg_gradient || theme.bg || '#060810';
  document.body.style.backgroundAttachment = 'fixed';
};

const clearOrgTheme = () => {
  const root = document.documentElement;
  ['--org-bg','--org-accent','--org-accent2','--org-accent3','--org-text',
   '--org-card-bg','--org-font','--org-scan','--org-badge-bg','--org-badge',
   '--org-bg-image','--org-bg-gradient'].forEach(v => root.style.removeProperty(v));
  document.body.style.fontFamily = '';
  document.body.style.background = '';
  document.body.style.backgroundAttachment = '';
  document.body.classList.remove('org-themed');
};
// Si el backend responde 401 (token expirado, inválido, o cuenta
// desactivada/eliminada tras la emisión del token), limpiamos la sesión
// local y forzamos vuelta a login. Evita que el usuario quede con un
// token "zombi" viendo errores 401 sueltos en cada llamada.
let isHandling401 = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isHandling401
        && !error.config?.skipAuthRedirect) {
      isHandling401 = true;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearOrgTheme();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const fetchAndApplyTheme = async (organizationId) => {
  if (!organizationId) return;
  try {
    const res = await api.get(`/organizations/${organizationId}`);
    console.log('🎨 Tema org:', res.data?.theme?.name);
    if (res.data?.theme) applyOrgTheme(res.data.theme);
  } catch (e) {
    console.warn('No se pudo cargar el tema:', e);
  }
};

// 2. Definir la lógica de redirección basada en roles
export const redirectByRole = (user) => {
  if (!user || !user.role) return '/dashboard'; // Fallback de seguridad
  switch(user.role) {
    case 'superadmin': 
      return '/superadmin';
    case 'admin':      
      return '/admin';
    default:           
      return '/dashboard';
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState]           = useState(null);
  const [token, setToken]              = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading]          = useState(true);
  
  const navigate = useNavigate(); // <-- 3. Inicializar useNavigate

    useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser  = localStorage.getItem('user');
    if (savedToken && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setToken(savedToken);
      setUserState(parsedUser);
      setIsAuthenticated(true);

      // Siempre refrescamos: así cogemos los permisos (features) al día
      api.get('/auth/me').then(res => {
        const fresh = res.data;
        localStorage.setItem('user', JSON.stringify(fresh));
        setUserState(fresh);
        fetchAndApplyTheme(fresh?.organization_id);
      }).catch(() => {
        fetchAndApplyTheme(parsedUser?.organization_id);
      });
    }
    setLoading(false);
  }, []);

  const setUser = (updater) => {
    setUserState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next) localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const setAuth = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(accessToken);
    setUserState(userData);
    setIsAuthenticated(true);
    fetchAndApplyTheme(userData?.organization_id);
  };

  const login = async (credentials) => {
    try {
      let response;
      if (credentials.access_code) {
        response = await api.post('/auth/login-by-code', { access_code: credentials.access_code });
      } else {
        const loginData = { email: credentials.email, password: credentials.password };
        if (credentials.organization_id) loginData.organization_id = credentials.organization_id;
        response = await api.post('/auth/login', loginData);
      }
      const { access_token, user: userData } = response.data;
      
      setAuth(access_token, userData);
      
      // <-- 4. Ejecutar la redirección tras iniciar sesión
      navigate(redirectByRole(userData)); 
      
      return userData;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error al iniciar sesión');
    }
  };

const register = async (userData) => {
  try {
    const response = await api.post('/auth/register', userData);
    // Ya no hay token: la cuenta queda pendiente de aprobación
    return response.data; // { status: 'pending', message: '...' }
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Error en el registro');
  }
};
  const logout = async () => {
    try {
      // El token sigue en localStorage, así que el interceptor lo adjunta
      await api.post('/auth/logout', null, { skipAuthRedirect: true });
    } catch (e) {
      // Si falla (red, token ya caducado...) cerramos igualmente en local
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUserState(null);
    setIsAuthenticated(false);
    clearOrgTheme();
    navigate('/login');
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const freshUser = response.data;
      localStorage.setItem('user', JSON.stringify(freshUser));
      setUserState(freshUser);
      return freshUser;
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const value = {
    user, setUser, refreshUser,
    token, isAuthenticated, loading,
    login, setAuth, register, logout,
    api,
    applyOrgTheme,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;